import { useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';

// @mui
import { useTheme } from '@mui/material/styles';
import { Grid, Box } from '@mui/material';

// Context and API routes
import { FetchContext } from 'src/context/FetchContext';
import { API_ROUTES } from 'src/services/constant';

// Components
import {
  AppCurrentVisits,
  AppWebsiteVisits,
  AppWidgetSummary,
} from '../sections/@dashboard/app';

// ----------------------------------------------------------------------

const { PRIVATE_ROUTE } = API_ROUTES;

export default function DashboardAppPage() {
  const theme = useTheme();
  const { authAxios } = useContext(FetchContext);

  const [stats, setStats] = useState({
    totalClients: 0,
    totalJobs: 0,
    totalActiveTasks: 0,
    totalCompletedTasks: 0,
    completedTasksPerMonth: [],
    totalBillableInvoiceAmount: 0,
    totalEstimatedCost: 0,
    currentYear: new Date().getFullYear(),
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardStatistics = async () => {
      try {
        setLoading(true);

        const { data: response } = await authAxios.get(
          PRIVATE_ROUTE?.GET_DASHBOARD_STATISTICS
        );

        if (response?.statusCode === 200 && response?.data) {
          setStats((previous) => ({
            ...previous,
            ...response.data,
          }));
        }
      } catch (error) {
        console.error(
          'Failed to fetch dashboard statistics:',
          error
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStatistics();
  }, [authAxios]);

  const completedTasksPerMonth = stats.completedTasksPerMonth || [];

  const chartLabels = completedTasksPerMonth.map((item) => {
    const label = item.label;

    // Convert a four-digit year to two digits
    return label.replace(/\b(20\d{2})\b/g, (_, year) => year.slice(-2));
  });

  const chartData = completedTasksPerMonth.map(
    (item) => Number(item.count) || 0
  );

  return (
    <>
      <Helmet>
        <title>Dashboard | Mevelsurvey</title>
      </Helmet>

      <Grid container spacing={3} sx={{ p: 4, pb: 0 }}>
        {/* Summary widgets */}
        <Grid item xs={12} sm={6} md={3}>
          <AppWidgetSummary
            title="Clients"
            total={loading ? 0 : stats.totalClients}
            icon="ant-design:user-outlined"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <AppWidgetSummary
            title="Jobs"
            total={loading ? 0 : stats.totalJobs}
            color="info"
            icon="ant-design:schedule-outlined"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <AppWidgetSummary
            title="Active Tasks"
            total={loading ? 0 : stats.totalActiveTasks}
            color="warning"
            icon="eva:clipboard-outline"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <AppWidgetSummary
            title="Completed Tasks"
            total={loading ? 0 : stats.totalCompletedTasks}
            color="error"
            icon="eva:checkmark-circle-2-outline"
          />
        </Grid>

        {/* Tasks Completed chart */}
        <Grid
          item
          xs={12}
          md={6}
          lg={8}
          sx={{ display: 'flex' }}
        >
          <Box
            sx={{
              width: '100%',
              height: 465,
              display: 'flex',
              '& > *': {
                width: '100%',
                height: '100%',
              },
            }}
          >
            <AppWebsiteVisits
              title="Tasks Completed"
              subheader="Monthly totals for the last 12 months"
              chartLabels={chartLabels}
              chartData={[
                {
                  name: 'Tasks Completed',
                  type: 'column',
                  fill: 'solid',
                  data: chartData,
                },
              ]}
            />
          </Box>
        </Grid>

        {/* Financial Data chart */}
        <Grid
          item
          xs={12}
          md={6}
          lg={4}
          sx={{ display: 'flex' }}
        >
          <Box
            sx={{
              width: '100%',
              height: 465,
              display: 'flex',
              '& > *': {
                width: '100%',
                height: '100%',
              },
            }}
          >
            <AppCurrentVisits
              title={`Financial Data (${stats.currentYear})`}
              chartData={[
                {
                  label: 'Billable Cost',
                  value: Number(stats.totalBillableInvoiceAmount) || 0,
                },
                {
                  label: 'Invoiced Amount',
                  value: Number(stats.totalEstimatedCost) || 0,
                },
              ]}
              chartColors={[
                theme.palette.primary.main,
                theme.palette.info.main,
              ]}
            />
          </Box>
        </Grid>
      </Grid>
    </>
  );
}